import { AxiosError, AxiosResponse } from 'axios';
import { useQuery } from 'react-query';

import { AgencyFilterDto, OrganizationResponseDto, PagedResponse } from '@bandido/shared/dtos';

import { QueryKeys } from '../common/types';
import { client } from '../common/services';

interface useGetTokenOrganizationsProps {
  onErrorCallback?: (_e: AxiosError) => void;
  agencyId?: number;
}

interface useGetOrganizationsByAgencyProps extends Omit<AgencyFilterDto, 'agencyId'>, useGetTokenOrganizationsProps {
  onErrorCallback?: (_e: AxiosError) => void;
  agencyId?: number;
}

export const useGetTokenOrganizations = (params?: useGetTokenOrganizationsProps | undefined) => {
  const { onErrorCallback } = params || {};

  return useQuery({
    queryKey: [QueryKeys.getTokenOrganizations],
    queryFn: async (): Promise<OrganizationResponseDto[] | undefined> => {
      try {
        const users: AxiosResponse<OrganizationResponseDto[]> = await client.get(`organizations/token`);

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
  });
};

export const useGetOrganizationsByAgency = (params?: useGetOrganizationsByAgencyProps | undefined) => {
  const { currentPage = 1, pageSize = 0, search, onErrorCallback, agencyId } = params || {};

  return useQuery({
    queryKey: [QueryKeys.getOrganizationsByAgencyId, currentPage, pageSize, search, agencyId],
    queryFn: async (): Promise<PagedResponse<OrganizationResponseDto> | undefined> => {
      try {
        const users: AxiosResponse<PagedResponse<OrganizationResponseDto>, any> = await client.get(
          `organizations/${agencyId}/list`,
          {
            params: {
              currentPage,
              pageSize,
              search,
            },
          },
        );

        return users.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(agencyId),
  });
};

interface UseGetOrganizationById {
  id?: number;
  onErrorCallback?: (_e: AxiosError) => void;
}

export const useGetOrganizationById = ({ id, onErrorCallback }: UseGetOrganizationById) =>
  useQuery({
    queryKey: [QueryKeys.getOrganizationById, id],
    queryFn: async (): Promise<OrganizationResponseDto | undefined> => {
      try {
        const result: AxiosResponse<OrganizationResponseDto, any> = await client.get(`organizations/${id}`);

        return result.data;
      } catch (e) {
        onErrorCallback && onErrorCallback(e as AxiosError);
      }
    },
    refetchOnWindowFocus: true,
    enabled: Boolean(id),
  });
